import React, { useState, useEffect, useRef } from "react";
import Picker from "emoji-picker-react";
import axiosInstance, { baseWebSocketURL } from "../axiosInstance.js";
import { localizeKey } from "../localize.js";
import { useNavigate } from "react-router-dom";
import { Button } from "@mantine/core";

export default function MyChatComponent({ chatId, language, myUserInfo, _userChatInfo, chatOwnerInfo, isDropdown=false, fillHeight=false }) {
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [typingNick, setTypingNick] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null); // { messageId, text }
  const [replyingTo, setReplyingTo] = useState(null); // { messageId, text, fromUserName, fragment? }
  // { messageId, fragment, x, y } — set when the user text-selects
  // inside a message bubble; drives the floating "Reply to selection"
  // button positioned near the selection.
  const [selectionInfo, setSelectionInfo] = useState(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const highlightTimeoutRef = useRef(null);
  const [userChatInfo, setUserChatInfo] = useState(_userChatInfo);

  // Highlighted fragment inside a parent message — set alongside the
  // outer boxShadow flash when clicking a reply preview that quoted a
  // specific fragment. Same 1500ms TTL.
  const [highlightedFragment, setHighlightedFragment] = useState(null); // { messageId, fragment }
  const flashMessage = (messageId, fragment = null) => {
    setHighlightedMessageId(messageId);
    setHighlightedFragment(fragment ? { messageId, fragment } : null);
    clearTimeout(highlightTimeoutRef.current);
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedMessageId(null);
      setHighlightedFragment(null);
    }, 1500);
  };

  /* ------------------------------------------------------- */
  /* Live link preview above input                           */
  /* ------------------------------------------------------- */
  const [livePreview, setLivePreview] = useState(null); // { previewTitle, previewDescription, previewImageUrl, previewSourceUrl }
  const [dismissedPreviewUrl, setDismissedPreviewUrl] = useState(null);
  const previewFetchTimeoutRef = useRef(null);

  const ws = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const typingTimeoutRef = useRef(null); // Add a ref to manage the typing timeout
  const draftTimeoutRef = useRef(null);
  const lastSavedDraftRef = useRef("");
  const draftLoadedRef = useRef(false);

  /* ------------------------------------------------------- */
  /* Auto-scroll down                                        */
  /* ------------------------------------------------------- */
  const messagesEndRef = useRef(null);
  const scrollRef = useRef(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  useEffect(() => {
    if (shouldAutoScroll) {
      // Scroll only THIS chat's own scroll container. Using
      // scrollIntoView() without options walks up every scrollable
      // ancestor, which inside a Mantine Drawer scrolls the drawer
      // body too and hides anything (thumbnail, tag editor) above the
      // chat.
      const scrollEl = scrollRef.current;
      if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
    }
  }, [messages]);
  // Hack when message is sent already.
  const scrollMessagesToBottom = () => {
    const scrollEl = scrollRef.current;
    if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
  };

  /* ------------------------------------------------------- */
  /* Message Menu                                            */
  /* ------------------------------------------------------- */
  const [menuOpenFor, setMenuOpenFor] = useState(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".message-menu")) {
        setMenuOpenFor(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ------------------------------------------------------- */
  /* Suggested Users                                         */
  /* ------------------------------------------------------- */
  const fetchUsers = (query) => {
    axiosInstance
      .get(`/searchUsers?userName=${encodeURIComponent(query)}`)
      .then((res) => setSuggestions(res.data || []))
      .catch(() => setSuggestions([]));
  };

  const onInputChange = (e) => {
    const val = e.target.value;
    const caret = e.target.selectionStart;
    setMessageText(val);

    const mention = findActiveMention(val, caret);
    if (mention) {
      fetchUsers(mention.fragment);
    } else {
      setSuggestions([]);
    }

    if (ws.current?.readyState === 1) {
      ws.current.send("_" + myUserInfo.myUserName);
    }
  };

  const selectUser = (user) => {
    const el = inputRef.current;
    if (!el) return;

    const caret = el.selectionStart;
    const mention = findActiveMention(messageText, caret);
    if (!mention) return;

    const { newText, newCaret } = replaceMention(
      messageText,
      mention.start,
      mention.end,
      user.userName
    );

    setMessageText(newText);
    setSuggestions([]);

    setTimeout(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = newCaret;
    }, 0);
  };

  /* ------------------------------------------------------- */
  /* Load Messages                                           */
  /* ------------------------------------------------------- */
  const fetchMessages = () => {
    axiosInstance
      .get(`/messages?chatId=${chatId}&chatOwnerId=${chatOwnerInfo.id}`)
      .then(res => setMessages(res.data.reverse()))
      .catch(() => {});
  };

  const fetchDraft = () => {
    axiosInstance
      .get(`/messages/drafts?chatId=${chatId}`)
      .then((res) => {
        const text = res.data?.text || "";
        setMessageText(text);
        lastSavedDraftRef.current = text;
      })
      .catch(() => {})
      .finally(() => {
        draftLoadedRef.current = true;
      });
  };

  const saveDraft = (text) => {
    if (!myUserInfo || !chatId) return;
    if (lastSavedDraftRef.current === text) return;

    lastSavedDraftRef.current = text;
    axiosInstance.post(`/messages/drafts`, { chatId, text }).catch(() => {});
  };

  const scheduleDraftSave = (text) => {
    clearTimeout(draftTimeoutRef.current);
    draftTimeoutRef.current = setTimeout(() => saveDraft(text), 700);
  };

  /* ------------------------------------------------------- */
  /* Init WebSocket                                          */
  /* ------------------------------------------------------- */
  const initWebSocket = () => {
    if (ws.current && (ws.current.readyState === 1 || ws.current.readyState === 0)) return;

    // Backend gates the WS handshake on JWT + chat participation, so
    // pass the token as a query param (browsers don't allow custom
    // headers on WebSocket connect).
    const token = localStorage.getItem('jwtToken') || '';
    const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';
    ws.current = new WebSocket(`${baseWebSocketURL}/chat/${chatId}${tokenQuery}`);

    ws.current.onmessage = (e) => {
      if (e.data.startsWith("_")) {
        const nick = e.data.substring(1);
        if (nick !== myUserInfo.myUserName) {
          setTypingNick(nick);

          // Clear the previous timeout and set a new one
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setTypingNick(null), 3000); 
        }
        return;
      }
      fetchMessages();
    };
  };

  /* ------------------------------------------------------- */
  /* Message Edit via main input                              */
  /* ------------------------------------------------------- */
  const startEdit = (msg) => {
    setReplyingTo(null);
    setEditingMessage({ messageId: msg.messageId, text: msg.text });
    setMessageText(msg.text);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const cancelEdit = () => {
    setEditingMessage(null);
    // setMessageText("");
  };

  const saveEdit = () => {
    if (!editingMessage || !messageText.trim()) return;

    axiosInstance
      .put(`/messages/${editingMessage.messageId}`, { text: messageText })
      .then((res) => {
        if (res.status === 200) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.messageId === editingMessage.messageId
                ? { ...msg, text: messageText, updatedTime: res.data.updatedTime }
                : msg
            )
          );
          setMessageText("");

          // ADDITIONAL MOBILE-FRIENDLY SCROLL
          setTimeout(scrollMessagesToBottom, 50);
        }
      })
      .catch((e) => console.error(e))
      .finally(() => {
        cancelEdit();
      });
  };

  /* ------------------------------------------------------- */
  /* Handlers                                                */
  /* ------------------------------------------------------- */
  const sendMessage = () => {
    if (!messageText.trim()) return;

    const match = messageText.match(/https?:\/\/[^\s]+/);
    const currentUrl = match ? match[0] : null;
    const skipLinkPreview = currentUrl && currentUrl === dismissedPreviewUrl;

    axiosInstance
      .post(`/messages?chatId=${chatId}&chatOwnerId=${chatOwnerInfo.id}`, {
        text: messageText,
        chatId,
        chatOwnerType: chatOwnerInfo.type,
        chatOwnerId: chatOwnerInfo.id,
        skipLinkPreview,
        parentMessageId: replyingTo?.messageId,
        parentMessageFragment: replyingTo?.fragment || undefined,
      })
      .then(res => {
        if (res.status === 200) {
          setMessages(prev => [...prev, res.data]);
          setMessageText("");
          setReplyingTo(null);
          setLivePreview(null);
          setDismissedPreviewUrl(null);
          clearTimeout(draftTimeoutRef.current);
          saveDraft("");

          // ADDITIONAL MOBILE-FRIENDLY SCROLL
          setTimeout(scrollMessagesToBottom, 50);
        }
      });
  };

  const subscribe = () => {
    axiosInstance.post(`/chats/${chatId}/participation`).then(() => {
      setUserChatInfo((prev) => ({...prev, participates: true}));
    });
  };

  const unsubscribe = () => {
    axiosInstance.delete(`/chats/${chatId}/participation`).then(() => {
     setUserChatInfo((prev) => ({...prev, participates: false}));
    });
  };

  const getLastOwnMessage = () => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.fromUserId === myUserInfo.myUserId && !m.deleted) {
        return m;
      }
    }
    return null;
  };

  /* ------------------------------------------------------- */
  /* Effects                                                 */
  /* ------------------------------------------------------- */
  useEffect(() => {
    if (!myUserInfo) return;

    initWebSocket();
    fetchMessages();
    fetchDraft();

    const interval = setInterval(() => initWebSocket(), 3000);
    return () => {
      if (ws.current?.readyState === 1 || ws.current?.readyState === 0) {
        ws.current.close();
      }
      clearInterval(interval);
    };
  }, [chatId, myUserInfo?.myUserId, chatOwnerInfo?.id]);

  useEffect(() => {
    if (!chatId) return;
    setMessageText("");
    setSuggestions([]);
    setTypingNick(null);
    setEditingMessage(null);
    setReplyingTo(null);
    lastSavedDraftRef.current = "";
    draftLoadedRef.current = false;
  }, [chatId]);

  // Auto-resize textarea on edit.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
  
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  }, [messageText]);

  useEffect(() => {
    return () => {
      // Clear the typing timeout on component unmount
      clearTimeout(typingTimeoutRef.current);
      clearTimeout(draftTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!myUserInfo || !chatId) return;
    if (!draftLoadedRef.current) return;
    scheduleDraftSave(messageText);
  }, [messageText, chatId, myUserInfo?.myUserId]);

  // Debounced link preview fetch as user types.
  useEffect(() => {
    const match = messageText.match(/https?:\/\/[^\s]+/);
    const url = match ? match[0] : null;

    if (!url) {
      setLivePreview(null);
      clearTimeout(previewFetchTimeoutRef.current);
      return;
    }
    if (url === dismissedPreviewUrl) {
      setLivePreview(null);
      return;
    }
    if (livePreview && livePreview.previewSourceUrl === url) {
      return; // Already fetched.
    }

    clearTimeout(previewFetchTimeoutRef.current);
    previewFetchTimeoutRef.current = setTimeout(() => {
      axiosInstance
        .get(`/linkPreview?url=${encodeURIComponent(url)}`)
        .then((res) => {
          const d = res.data || {};
          if (!d.previewTitle && !d.previewDescription && !d.previewImageUrl) {
            setLivePreview(null);
            return;
          }
          setLivePreview({
            previewTitle: d.previewTitle,
            previewDescription: d.previewDescription,
            previewImageUrl: d.previewImageUrl,
            previewSourceUrl: d.previewSourceUrl || url,
          });
        })
        .catch(() => setLivePreview(null));
    }, 500);
  }, [messageText, dismissedPreviewUrl, livePreview]);

  // Clear preview state when switching chats.
  useEffect(() => {
    setLivePreview(null);
    setDismissedPreviewUrl(null);
    clearTimeout(previewFetchTimeoutRef.current);
  }, [chatId]);

  /* ------------------------------------------------------- */
  /* UI Helpers                                              */
  /* ------------------------------------------------------- */
  const linkify = (text) =>
    text.replace(
      /(https?:\/\/[^\s]+)/g,
      (m) => `<a class="chat-link" href="${m}" target="_blank">${m}</a>`
    );

  const mentionify = (t) => t.replace(/@([a-zA-Z0-9_.-]+)/g, `<span class="chat-mention">@$1</span>`);

  const italizeAux = (t) => {
    if (!t.startsWith('aux: ')) return t;

    return `<em class="chat-deleted">${t.slice(5)}</em>`;
  };

  /* ------------------------------------------------------- */
  /* ------------------------------------------------------- */
  /* Emojis                                                  */
  /* ------------------------------------------------------- */
  const [emojiQuery, setEmojiQuery] = useState(null);
  const [filteredEmojis, setFilteredEmojis] = useState([]);
  const [selectedEmojiIndex, setSelectedEmojiIndex] = useState(0);
  const emojiPickerRef = useRef(null);

  useEffect(() => {
    if (selectedEmojiIndex >= filteredEmojis.length) {
      setSelectedEmojiIndex(0);
    }
  }, [filteredEmojis, selectedEmojiIndex]);

  useEffect(() => {
    const container = emojiPickerRef.current;
    if (!container) return;
    const el = container.children[selectedEmojiIndex];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  }, [selectedEmojiIndex]);

  const onEmojiSelect = (emoji) => {
    const el = inputRef.current;
    const caret = el.selectionStart;
    const before = messageText.slice(0, caret);
    const after = messageText.slice(caret);
    // replace :query with emoji + space
    const newBefore = before.replace(/:[a-zA-Z0-9_]*$/, emoji + " ");
    const newText = newBefore + after;
    setMessageText(newText);
    setEmojiQuery(null);
    setShowEmojiPicker(false);
    setTimeout(() => {
      el.focus();
      const newCaret = newBefore.length;
      el.selectionStart = el.selectionEnd = newCaret;
    }, 0);
  };

  const replyToMessage = (msg, fragment = null) => {
    setEditingMessage(null);
    setReplyingTo({
      messageId: msg.messageId,
      text: msg.text,
      fromUserName: msg.fromUserName,
      fragment: fragment || null,
    });
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // Detect a text selection inside a message bubble and expose the
  // fragment + position via selectionInfo so the floating button can
  // render near it. Runs on mouseup / touchend anywhere in the chat.
  const handleSelectionMaybe = () => {
    const sel = typeof window !== 'undefined' ? window.getSelection() : null;
    if (!sel || sel.isCollapsed) { setSelectionInfo(null); return; }
    const text = sel.toString().trim();
    if (!text) { setSelectionInfo(null); return; }
    // Both endpoints must live in the same message text container.
    const findMsgEl = (n) => {
      while (n && n.nodeType) {
        if (n.nodeType === 1 && n.getAttribute && n.getAttribute('data-msg-text-id')) return n;
        n = n.parentNode;
      }
      return null;
    };
    const a = findMsgEl(sel.anchorNode);
    const b = findMsgEl(sel.focusNode);
    if (!a || a !== b) { setSelectionInfo(null); return; }
    const messageId = a.getAttribute('data-msg-text-id');
    let rect = null;
    try {
      const range = sel.getRangeAt(0);
      rect = range.getBoundingClientRect();
    } catch (_) { setSelectionInfo(null); return; }
    if (!rect || (rect.width === 0 && rect.height === 0)) { setSelectionInfo(null); return; }
    // Cap the fragment to the DB limit (500) — server also truncates
    // but chopping here keeps the preview honest.
    const fragment = text.length > 500 ? text.slice(0, 500) : text;
    setSelectionInfo({
      messageId,
      fragment,
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
  };

  const [modalImage, setModalImage] = useState(null);

  const renderMessage = (m, index) => {
    const isMine = m.fromUserId === myUserInfo.myUserId;
    const isDeleted = m.deleted;
    const nextMessage = messages[index + 1];
    const showAvatar = !isMine && (!nextMessage || nextMessage.fromUserId !== m.fromUserId);
  
    const handleDelete = () => {
      if (!window.confirm(localizeKey("confirmDeleteMessage", language))) return;
      axiosInstance
        .delete(`/messages/${m.messageId}`)
        .then(() => {
          setMessages(prev =>
            prev.map(msg =>
              msg.messageId === m.messageId ? { ...msg, text: localizeKey("deletedBySender", language), deleted: true } : msg
            )
          );
        })
        .catch(console.error);
    };
  
    const parentMessage = m.parentMessageId
      ? messages.find((mm) => mm.messageId === m.parentMessageId)
      : null;

    const timestamp = m.updatedTime ? new Date(m.updatedTime) : new Date(m.sentTime);
    const now = new Date();
    const isToday = timestamp.toDateString() === now.toDateString();
    const isThisYear = timestamp.getFullYear() === now.getFullYear();
  
    const localizedTimestamp = isToday
      ? timestamp.toLocaleTimeString(language === "ru" ? "ru-RU" : "en-US", { hour: '2-digit', minute: '2-digit' })
      : timestamp.toLocaleDateString(language === "ru" ? "ru-RU" : "en-US", {
          hour: '2-digit',
          minute: '2-digit',
          day: "2-digit",
          month: "short",
          ...(isThisYear ? {} : { year: "numeric" })
        });


  
    return (
      <div
        id={`message-${m.messageId}`}
        className={`mb-2 flex ${
          isMine ? "justify-end pr-3" : "justify-start pl-2"
        }`}
      >
        <div
          className={`flex gap-1 max-w-[80%] min-w-0 items-end ${
            isMine ? "ml-auto" : "mr-auto"
          }`}
        >
          {/* Avatar */}
          {showAvatar ? (
            <img
              src={m.fromUserAvatarUrl}
              className="w-8 h-8 rounded-full cursor-pointer self-end flex-shrink-0"
              onClick={() => navigate(`/users/${m.fromUserId}`)}
              alt=""
            />
          ) : (
            <div className="w-8 flex-shrink-0" />
          )}
    
          {/* Message block */}
          <div className="flex flex-col relative min-w-0">
            {/* Bubble — text is normally selectable; the action menu
                is opened by clicking the ⋯ button next to the
                timestamp below, not by tapping the bubble. */}
            <div
              className="p-2 rounded-xl text-sm whitespace-pre-wrap break-words"
              style={{
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                background: isMine ? "var(--mantine-primary-color-filled)" : "light-dark(var(--mantine-color-gray-1), var(--mantine-color-default))",
                color: isMine ? "white" : undefined,
                transition: "box-shadow 300ms ease-out",
                boxShadow: highlightedMessageId === m.messageId ? "0 0 0 3px var(--mantine-color-blue-text)" : undefined,
                ...(isDeleted ? { fontStyle: "italic", color: "var(--mantine-color-dimmed)" } : {}),
              }}
            >
              {m.parentMessageId && (
                <div
                  className="mb-1 pl-2 pr-2 py-1 rounded text-xs cursor-pointer"
                  style={{
                    borderLeft: "3px solid " + (isMine ? "rgba(255,255,255,0.6)" : "var(--mantine-color-blue-text)"),
                    background: isMine ? "rgba(255,255,255,0.12)" : "var(--mantine-color-default)",
                    color: isMine ? "rgba(255,255,255,0.9)" : "var(--mantine-color-dimmed)",
                    maxWidth: "100%",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const el = document.getElementById(`message-${m.parentMessageId}`);
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                    flashMessage(m.parentMessageId, m.parentMessageFragment || null);
                  }}
                >
                  <div className="font-semibold truncate">
                    {parentMessage?.fromUserName || (language === "en" ? "Reply" : "Ответ")}
                  </div>
                  <div className="truncate">
                    {m.parentMessageFragment
                      ? (language === 'en' ? `“${m.parentMessageFragment}”` : `«${m.parentMessageFragment}»`)
                      : (parentMessage ? parentMessage.text : (language === "en" ? "(message unavailable)" : "(сообщение недоступно)"))}
                  </div>
                </div>
              )}
              <span
                data-msg-text-id={m.messageId}
                className="select-text"
                dangerouslySetInnerHTML={{
                  __html: (() => {
                    const fmt = (t) => italizeAux(mentionify(linkify(t)));
                    if (
                      highlightedFragment &&
                      String(highlightedFragment.messageId) === String(m.messageId)
                    ) {
                      const idx = m.text.indexOf(highlightedFragment.fragment);
                      if (idx >= 0) {
                        const before = m.text.slice(0, idx);
                        const middle = m.text.slice(idx, idx + highlightedFragment.fragment.length);
                        const after = m.text.slice(idx + highlightedFragment.fragment.length);
                        // Inline background so it renders identically on both
                        // dark and light theme regardless of tailwind purge.
                        return (
                          fmt(before) +
                          `<mark style="background: rgba(255, 213, 79, 0.6); color: inherit; border-radius: 3px; padding: 0 2px;">` +
                          fmt(middle) +
                          '</mark>' +
                          fmt(after)
                        );
                      }
                    }
                    return fmt(m.text);
                  })(),
                }}
              />
            </div>
    
            {/* Link preview */}
            {(m.previewTitle || m.previewDescription || m.previewImageUrl) && !isDeleted && (
              <>
                {m.previewTitle || m.previewDescription ? (
                  // Full preview with link
                  <a
                    href={m.previewSourceUrl || "#"}
                    target="_blank"
                    className="flex mt-2 rounded overflow-hidden cursor-pointer max-w-full min-w-0"
                    style={{
                      background: "var(--mantine-color-default)",
                      border: "1px solid var(--mantine-color-default-border)",
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = "var(--mantine-color-default-hover)"}
                    onMouseOut={(e) => e.currentTarget.style.background = "var(--mantine-color-default)"}
                  >
                    {m.previewImageUrl && (
                      <img
                        src={m.previewImageUrl}
                        className="w-20 h-20 object-cover flex-shrink-0"
                        alt="link preview"
                      />
                    )}
                    <div className="p-2 flex flex-col justify-center text-sm max-w-xs min-w-0">
                      {m.previewTitle && <div className="font-semibold truncate">{m.previewTitle}</div>}
                      {m.previewDescription && (
                        <div style={{ color: "var(--mantine-color-dimmed)" }} className="truncate">{m.previewDescription}</div>
                      )}
                    </div>
                  </a>
                ) : (
                  // Only image → open modal on click
                  <div
                    className="inline-block mt-2 cursor-pointer"
                    onClick={() => setModalImage(m.previewImageUrl)}
                  >
                    <img
                      src={m.previewImageUrl}
                      className="w-20 h-20 object-cover rounded"
                      style={{ border: "1px solid var(--mantine-color-default-border)" }}
                      alt="link preview"
                    />
                  </div>
                )}

                {/* Modal for full-size image */}
                {modalImage && (
                  <div
                    className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
                    onClick={() => setModalImage(null)}
                  >
                    <img src={modalImage} className="max-w-full max-h-full" alt="full preview" />
                  </div>
                )}
              </>
            )}
    
            {/* Timestamp + Reply */}
            <div
              className={`text-xs mt-1 flex items-center gap-2 ${
                isMine ? "justify-end" : "justify-start"
              }`}
              style={{ color: "var(--mantine-color-dimmed)" }}
            >
              {!isDeleted && (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Message actions"
                  className="cursor-pointer select-none leading-none px-1"
                  style={{ color: "var(--mantine-color-dimmed)" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpenFor(menuOpenFor === m.messageId ? null : m.messageId);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setMenuOpenFor(menuOpenFor === m.messageId ? null : m.messageId);
                    }
                  }}
                >
                  ⋯
                </span>
              )}
              <span>
                {localizedTimestamp}
                {m.updatedTime && (
                  <span className="ml-1" style={{ color: "var(--mantine-color-dimmed)" }}>
                    ({localizeKey("edited", language)})
                  </span>
                )}
              </span>
    
              {!isDeleted && (
                <span
                  className="cursor-pointer hover:underline"
                  style={{ color: "var(--mantine-color-blue-text)" }}
                  onClick={() => replyToMessage(m)}
                >
                  {language === "en" ? "Reply" : "Ответить"}
                </span>
              )}
            </div>
    
            {/* Message menu — Copy text for everyone, Edit/Delete only
                for the message's own author. */}
            {!isDeleted && menuOpenFor === m.messageId && (
              <div
                className={`message-menu absolute ${
                  isMine ? "right-0" : "left-0"
                } bottom-full mb-1 min-w-max rounded shadow-lg z-50`}
                style={{
                  background: "var(--mantine-color-body)",
                  border: "1px solid var(--mantine-color-default-border)",
                }}
              >
                <div
                  className="px-2 py-1 cursor-pointer text-sm"
                  onMouseOver={(e) => e.currentTarget.style.background = "var(--mantine-color-default-hover)"}
                  onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
                  onClick={() => {
                    if (navigator.clipboard && m.text) {
                      navigator.clipboard.writeText(m.text).catch(() => {});
                    }
                    setMenuOpenFor(null);
                  }}
                >
                  {localizeKey("copyText", language)}
                </div>
                {isMine && (
                  <>
                    <div
                      className="px-2 py-1 cursor-pointer text-sm"
                      onMouseOver={(e) => e.currentTarget.style.background = "var(--mantine-color-default-hover)"}
                      onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
                      onClick={() => {
                        startEdit(m);
                        setMenuOpenFor(null);
                      }}
                    >
                      {localizeKey("edit", language)}
                    </div>
                    <div
                      className="px-2 py-1 cursor-pointer text-sm"
                      style={{ color: 'var(--mantine-color-red-text)' }}
                      onMouseOver={(e) => e.currentTarget.style.background = "var(--mantine-color-default-hover)"}
                      onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
                      onClick={() => {
                        handleDelete();
                        setMenuOpenFor(null);
                      }}
                    >
                      {localizeKey("delete", language)}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };


      // console.log("myUserInfo:", myUserInfo);
      // console.log("isDropdown:", isDropdown);

  // In dropdown mode we live inside a Mantine Drawer alongside a
  // thumbnail + tag editor header. The default 499/640px height pushes
  // the input row below the drawer's bottom edge, so use a shorter
  // fixed height there. fillHeight still wins when explicitly set.
  const containerClassName = fillHeight
    ? "w-full h-full flex flex-col min-h-0 min-w-0 overflow-x-hidden px-3 py-2 relative"
    : isDropdown
      ? "w-full h-[20rem] md:h-[24rem] flex flex-col min-w-0 overflow-x-hidden px-3 py-2 relative"
      : "w-full h-[31.2rem] md:h-[40rem] flex flex-col min-w-0 overflow-x-hidden px-3 py-2 relative";

  return (
    <div className={containerClassName}>

      {/* Messages */}
      <div
        className="flex-1 min-h-0 min-w-0 overflow-y-auto relative"
        style={{ overscrollBehavior: 'contain' }}
        ref={scrollRef}
        onScroll={() => {
          const el = scrollRef.current;
          if (!el) return;
          setShouldAutoScroll(el.scrollHeight - el.scrollTop - el.clientHeight < 40);
        }}
        onMouseUp={handleSelectionMaybe}
        onTouchEnd={handleSelectionMaybe}
      >
        {messages.map(renderMessage)}
        <div ref={messagesEndRef} />
      </div>
      {selectionInfo && (() => {
        const target = messages.find((mm) => String(mm.messageId) === String(selectionInfo.messageId));
        if (!target) return null;
        return (
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onClick={(e) => {
              e.stopPropagation();
              replyToMessage(target, selectionInfo.fragment);
              setSelectionInfo(null);
              if (window.getSelection) window.getSelection().removeAllRanges();
            }}
            style={{
              position: 'fixed',
              left: `${selectionInfo.x}px`,
              top: `${Math.max(8, selectionInfo.y - 36)}px`,
              transform: 'translateX(-50%)',
              zIndex: 40,
              padding: '4px 10px',
              borderRadius: 6,
              background: 'var(--mantine-primary-color-filled)',
              color: 'white',
              fontSize: 12,
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
              cursor: 'pointer',
            }}
          >
            {language === 'en' ? 'Reply to selection' : 'Ответ на фрагмент'}
          </button>
        );
      })()}

      {/* Typing indicator */}
      {typingNick && (
        <div className="text-xs pl-2 py-0.5" style={{ color: "var(--mantine-color-dimmed)" }}>
          {typingNick + (language === "ru" ? " печатает..." : " is typing...")}
        </div>
      )}

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div
          className="absolute bottom-16 left-2 w-64 rounded-xl shadow-lg z-50 max-h-64 overflow-auto"
          style={{
            background: "var(--mantine-color-body)",
            border: "1px solid var(--mantine-color-default-border)",
          }}
        >
          {suggestions.map((u) => (
            <div
              key={u.id}
              onClick={() => selectUser(u)}
              className="flex items-center gap-2 p-2 cursor-pointer"
              onMouseOver={(e) => e.currentTarget.style.background = "var(--mantine-color-default-hover)"}
              onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
            >
              <img src={u.avatarUrl} className="w-7 h-7 rounded-full" alt="" />
              <span className="text-sm">@{u.userName}</span>
            </div>
          ))}
        </div>
      )}

      {/* Slim Emoji-picker */}
      {showEmojiPicker && emojiQuery !== null && filteredEmojis.length > 0 && (
        <div
          ref={emojiPickerRef}
          className="absolute bottom-16 left-2 right-2 flex space-x-1 p-1 rounded z-50 overflow-x-auto"
          style={{
            background: "var(--mantine-color-body)",
            border: "1px solid var(--mantine-color-default-border)",
          }}
        >
          {filteredEmojis.map((e, idx) => (
            <span
              key={e.name}
              className="cursor-pointer text-xl px-1 rounded"
              style={{
                background: idx === selectedEmojiIndex ? "var(--mantine-color-default-hover)" : "transparent",
              }}
              onClick={() => onEmojiSelect(e.emoji)}
              onMouseEnter={() => setSelectedEmojiIndex(idx)}
            >
              {e.emoji}
            </span>
          ))}
        </div>
      )}

      {/* Input */}
      {myUserInfo && (
        <div className="relative mt-2 min-w-0">

          {/* Live link preview above input */}
          {livePreview && !editingMessage && (
            <div
              className="flex items-stretch mb-1 rounded overflow-hidden"
              style={{
                background: "var(--mantine-color-default)",
                border: "1px solid var(--mantine-color-default-border)",
              }}
            >
              {livePreview.previewImageUrl && (
                <img
                  src={livePreview.previewImageUrl}
                  className="w-14 h-14 object-cover flex-shrink-0"
                  alt=""
                />
              )}
              <div className="flex-1 min-w-0 p-2 text-sm flex flex-col justify-center">
                {livePreview.previewTitle && (
                  <div className="font-semibold truncate">{livePreview.previewTitle}</div>
                )}
                {livePreview.previewDescription && (
                  <div className="truncate" style={{ color: "var(--mantine-color-dimmed)" }}>
                    {livePreview.previewDescription}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="px-2 self-start text-lg leading-none"
                style={{ color: "var(--mantine-color-dimmed)" }}
                onClick={() => {
                  setDismissedPreviewUrl(livePreview.previewSourceUrl);
                  setLivePreview(null);
                }}
                aria-label="dismiss preview"
              >
                ×
              </button>
            </div>
          )}

          {/* Reply banner above input */}
          {replyingTo && !editingMessage && (
            <div className="text-sm mb-1 break-words flex justify-between items-start" style={{ color: "var(--mantine-color-dimmed)" }}>
              <span className="flex-1 min-w-0">
                <span className="font-bold" style={{ color: "var(--mantine-color-blue-text)" }}>
                  {language === "en" ? "Reply to " : "Ответ "}{replyingTo.fromUserName || ""}
                </span>
                <br />
                <span className="block truncate">
                  {replyingTo.fragment
                    ? (language === 'en' ? `“${replyingTo.fragment}”` : `«${replyingTo.fragment}»`)
                    : replyingTo.text}
                </span>
              </span>
              <Button
                variant="subtle"
                size="sm"
                className="ml-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setReplyingTo(null);
                }}
              >
                ✕
              </Button>
            </div>
          )}

          {/* Original message above input */}
          {editingMessage && (
            <div className="text-sm mb-1 break-words flex justify-between items-start" style={{ color: "var(--mantine-color-dimmed)" }}>
              <span className="flex-1">
                <span className="font-bold" style={{ color: "var(--mantine-color-blue-text)" }}>
                  {language === "en" ? "Edit message" : "Редактирование сообщения"}
                </span>
                <br />
                {editingMessage.text}
              </span>
              <Button
                variant="subtle"
                size="sm"
                className="ml-2"
                onClick={(e) => {
                  e.stopPropagation();
                  cancelEdit(); // input text stays because we removed setMessageText("")
                }}
              >
                ✕
              </Button>
            </div>
          )}

          <textarea
            ref={inputRef}
            value={messageText}
            onChange={(e) => {
              setMessageText(e.target.value);

              // Auto-resize textarea
              const el = e.target;
              el.style.height = "auto"; // reset height
              el.style.height = el.scrollHeight + "px";

              // Scroll messages down as textarea grows
              const scrollEl = scrollRef.current;
              if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;

              // Check for mentions
              const caret = el.selectionStart;
              const mention = findActiveMention(e.target.value, caret);
              if (mention) fetchUsers(mention.fragment);
              else setSuggestions([]);

              // Typing indicator via WebSocket
              if (ws.current?.readyState === 1) {
                ws.current.send("_" + myUserInfo.myUserName);
              }

              // --- emoji query handling ---
              const val = e.target.value;
              const char = val[caret - 1];
              if (char === ":") {
                setEmojiQuery("");
                setShowEmojiPicker(true);
                setFilteredEmojis(EMOJIS); // show all initially
              } else if (emojiQuery !== null) {
                // we are in emoji-query mode
                // find start of “:query”
                const substr = val.slice(0, caret);
                const match = substr.match(/:([a-zA-Z0-9_]*)$/);
                if (match) {
                  const q = match[1];
                  setEmojiQuery(q);
                  setFilteredEmojis(
                    q === ""
                      ? EMOJIS                          // ← show all emojis when query is empty
                      : EMOJIS.filter((e) =>
                          e.name.startsWith(q.toLowerCase())
                        )
                  );
                } else {
                  // token broken or removed
                  setEmojiQuery(null);
                  setShowEmojiPicker(false);
                }
              }
            }}
            rows={1}
            className="w-full pr-14 pl-2 pt-3 pb-3 rounded resize-none overflow-y-auto max-h-40 focus:outline-none"
            style={{ background: "light-dark(var(--mantine-color-gray-1), var(--mantine-color-default))", color: "var(--mantine-color-text)", border: "light-dark(1px solid var(--mantine-color-default-border), none)" }}
            placeholder={language === "en" ? "Type a message…" : "Напишите сообщение…"}
            onKeyDown={(e) => {
              const isMobile = /Mobi|Android/i.test(navigator.userAgent);
              const emojiPickerOpen = showEmojiPicker && emojiQuery !== null && filteredEmojis.length > 0;

              if (e.key === "Backspace" && emojiQuery !== null) {
                // if query becomes empty, hide
                const caret = e.target.selectionStart;
                const before = messageText.slice(0, caret);
                if (before.match(/:$/)) {
                  setEmojiQuery(null);
                  setShowEmojiPicker(false);
                }
              }
              if (emojiPickerOpen && !isMobile && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
                e.preventDefault();
                setSelectedEmojiIndex((i) => {
                  const n = filteredEmojis.length;
                  return e.key === "ArrowRight" ? (i + 1) % n : (i - 1 + n) % n;
                });
                return;
              }
              if (emojiPickerOpen && !isMobile && e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                const picked = filteredEmojis[selectedEmojiIndex] || filteredEmojis[0];
                if (picked) onEmojiSelect(picked.emoji);
                return;
              }
              if (e.key === "Enter" && !e.shiftKey) {
                if (isMobile) {
                  setEmojiQuery(null);
                  setShowEmojiPicker(false);
                  return; // on mobile, let the user tap the Send button
                }

                e.preventDefault();
                editingMessage ? saveEdit() : sendMessage();
                setEmojiQuery(null);
                setShowEmojiPicker(false);
              }
              if (e.key === "Escape" && editingMessage) cancelEdit();
              if (e.key === "Escape" && replyingTo) setReplyingTo(null);
              if (e.key === "Escape" && emojiQuery !== null) {
                setEmojiQuery(null);
                setShowEmojiPicker(false);
              }
              if (e.key === "ArrowUp" && !messageText.trim() && !editingMessage) {
                const last = getLastOwnMessage();
                if (last) startEdit(last);
              }
            }}
          />

          {
          messageText.trim() && (
            <Button
              variant="filled"
              color="blue"
              size="xs"
              style={{ position: 'absolute', right: 6, bottom: 13 }}
              onClick={editingMessage ? saveEdit : sendMessage}
            >
              {/* {editingMessage ? (language === "en" ? "Save" : "Сохранить") : (language === "en" ? "Send" : "Отправить")} */}
              &gt;
            </Button>
          )
          }
        </div>
      )}

    </div>
  );
}

// Finds an active @mention token the user is editing
export function findActiveMention(text, caretPos) {
  const left = text.slice(0, caretPos);
  const at = left.lastIndexOf("@");
  if (at === -1) return null;
  if (at > 0 && !/\s/.test(left[at - 1])) return null;
  const fragment = left.slice(at + 1);
  if (!/^[a-zA-Z0-9_.-]*$/.test(fragment)) return null;
  return { start: at, end: caretPos, fragment };
}

// Replace mention token with @username and restore caret
export function replaceMention(text, start, end, username) {
  const before = text.slice(0, start);
  const after = text.slice(end);
  const inserted = `@${username} `;
  return { newText: before + inserted + after, newCaret: before.length + inserted.length };
}

// small sample emoji dataset — expand as needed
const EMOJIS = [
  { name: "rofl", emoji: "🤣" },
  { name: "smile", emoji: "😄" },
  { name: "grin", emoji: "😁" },
  { name: "joy", emoji: "😂" },
  { name: "heart", emoji: "❤️" },
  { name: "thumbsup", emoji: "👍" },
  { name: "cry", emoji: "😢" },
  { name: "angry", emoji: "😠" },
  { name: "party", emoji: "🥳" },
  { name: "see-no-evil", emoji: "🙈" },
  { name: "laugh", emoji: "😆" },
  { name: "wink", emoji: "😉" },
  { name: "sunglasses", emoji: "😎" },
  { name: "thinking", emoji: "🤔" },
  { name: "clap", emoji: "👏" },
  { name: "pray", emoji: "🙏" },
  { name: "fire", emoji: "🔥" },
  { name: "star", emoji: "⭐" },
  { name: "100", emoji: "💯" },
  { name: "check", emoji: "✔️" },
  { name: "cross", emoji: "❌" },
  { name: "rocket", emoji: "🚀" },
  { name: "tada", emoji: "🎉" },
  { name: "coffee", emoji: "☕" },
  { name: "cake", emoji: "🎂" },
  { name: "dog", emoji: "🐶" },
  { name: "cat", emoji: "🐱" },
  { name: "unicorn", emoji: "🦄" },
  { name: "dragon", emoji: "🐉" },
  // … extend or load from full list
];
